import {
  getGetAllAccountsQueryKey,
  refreshAccountBalance,
  useCreateAccount,
  useDeleteAccount,
  useGetAllAccounts,
  useUpdateAccount,
} from '@/api/generated/accounts/accounts.ts';
import type { Account } from '@/api/generated/model';
import {
  computeNetWorthTotals,
  filterAccountsForDisplay,
  groupAccountsByType,
} from '@/features/accounts/buildNetWorthHistory';
import { type AccountFormData, toAccountInput } from '@/features/accounts/toAccountInput';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/i18n/language-hooks';
import { FILTER_ALL } from '@/lib/filterConstants';
import { useDefaultCurrency } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

export type { AccountFormData } from '@/features/accounts/toAccountInput';

export function useAccountsPage() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const defaultCurrency = useDefaultCurrency();

  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<string>(FILTER_ALL);
  const [selectedCategory, setSelectedCategory] = useState<string>(FILTER_ALL);

  const { data: accountsResponse, isLoading, error: accountsError } = useGetAllAccounts();
  const accounts = useMemo(() => accountsResponse?.items ?? [], [accountsResponse]);
  const createAccountMutation = useCreateAccount();
  const updateAccountMutation = useUpdateAccount();
  const deleteAccountMutation = useDeleteAccount();

  const filteredAccounts = useMemo(
    () => filterAccountsForDisplay(accounts, selectedAccountType, selectedCategory),
    [accounts, selectedAccountType, selectedCategory]
  );

  const groupedAccounts = useMemo(() => groupAccountsByType(filteredAccounts), [filteredAccounts]);

  const totals = useMemo(() => computeNetWorthTotals(accounts), [accounts]);

  const activeFiltersCount = [
    selectedAccountType !== FILTER_ALL ? selectedAccountType : null,
    selectedCategory !== FILTER_ALL ? selectedCategory : null,
  ].filter(Boolean).length;

  const clearAllFilters = useCallback(() => {
    setSelectedAccountType(FILTER_ALL);
    setSelectedCategory(FILTER_ALL);
  }, []);

  const error = accountsError ?? null;

  const handleAddAccount = useCallback(
    async (data: AccountFormData) => {
      const payload = toAccountInput(data, defaultCurrency);
      if (!payload) {
        toast({
          title: t('accounts.toast.createError'),
          description: t('accounts.toast.missingFields'),
          variant: 'destructive',
        });
        return;
      }
      await createAccountMutation.mutateAsync({ data: payload });
      await queryClient.invalidateQueries({ queryKey: getGetAllAccountsQueryKey() });
    },
    [createAccountMutation, defaultCurrency, queryClient, t]
  );

  const handleEditAccount = useCallback(
    async (data: AccountFormData, accountOverride?: Account | null) => {
      const account = accountOverride ?? selectedAccount;
      const payload = toAccountInput(data, defaultCurrency);
      if (!account || !payload) {
        toast({
          title: t('accounts.toast.updateError'),
          description: t('accounts.toast.missingFields'),
          variant: 'destructive',
        });
        return;
      }
      await updateAccountMutation.mutateAsync({
        id: account.id,
        data: payload,
      });
      await queryClient.invalidateQueries({ queryKey: getGetAllAccountsQueryKey() });
      setSelectedAccount(null);
    },
    [defaultCurrency, queryClient, selectedAccount, t, updateAccountMutation]
  );

  const handleDeleteAccount = useCallback(async () => {
    if (!selectedAccount) return;
    await deleteAccountMutation.mutateAsync({ id: selectedAccount.id });
    setIsDeleteDialogOpen(false);
    setSelectedAccount(null);
  }, [deleteAccountMutation, selectedAccount]);

  const handleRefreshBalance = useCallback(
    async (account: Account) => {
      try {
        await refreshAccountBalance(account.id);
        await queryClient.invalidateQueries({ queryKey: getGetAllAccountsQueryKey() });
        toast({
          title: t('accounts.toast.refreshSuccess'),
          description: t('accounts.toast.refreshDescription'),
        });
      } catch (refreshError) {
        console.error('Failed to refresh balance:', refreshError);
        toast({
          title: t('accounts.toast.refreshError'),
          description:
            refreshError instanceof Error
              ? refreshError.message
              : t('accounts.toast.refreshErrorDescription'),
          variant: 'destructive',
        });
      }
    },
    [queryClient, t]
  );

  return {
    accounts,
    filteredAccounts,
    groupedAccounts,
    totals,
    isLoading,
    error,
    selectedAccount,
    setSelectedAccount,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedAccountType,
    setSelectedAccountType,
    selectedCategory,
    setSelectedCategory,
    activeFiltersCount,
    clearAllFilters,
    handleAddAccount,
    handleEditAccount,
    handleDeleteAccount,
    handleRefreshBalance,
  };
}
